<script setup lang="ts">
import MarkdownIt from 'markdown-it';

type Segment = { html: string } | { lang: string; code: string };

const md = new MarkdownIt({
    html: true,
    breaks: true
});

const props = defineProps({
    modelValue: {
        type: String,
        required: true
    }
});

const segments = computed(() => {
    const result: Segment[] = [];
    let pending: ReturnType<typeof md.parse> = [];
    const flush = () => {
        if (pending.length) {
            result.push({ html: md.renderer.render(pending, md.options, {}) });
            pending = [];
        }
    };

    for (const token of md.parse(props.modelValue, {})) {
        if (token.type === 'fence' && token.level === 0) {
            flush();
            result.push({ lang: token.info.trim().split(/\s+/)[0] ?? '', code: token.content });
        } else {
            pending.push(token);
        }
    }
    flush();
    return result;
});

const copiedIndex = ref<number | null>(null);
let copyResetTimer: ReturnType<typeof setTimeout> | null = null;

const copy = async (code: string, index: number) => {
    try {
        await navigator.clipboard.writeText(code);
    } catch {
        notifyError('Could not copy to clipboard');
        return;
    }
    copiedIndex.value = index;
    if (copyResetTimer) {
        clearTimeout(copyResetTimer);
    }
    copyResetTimer = setTimeout(() => {
        copiedIndex.value = null;
    }, 1500);
};
</script>

<template>
    <div>
        <template v-for="(segment, index) in segments" :key="index">
            <div
                v-if="'code' in segment"
                class="my-2 rounded-lg overflow-hidden bg-[var(--main-grey-color)]"
            >
                <div class="flex items-center justify-between px-2 py-1 text-xs uppercase tracking-wide text-[var(--primary-text-muted)] border-b border-[color:var(--main-border-color)]">
                    <span>{{ segment.lang }}</span>
                    <button
                        type="button"
                        class="inline-flex items-center justify-center w-6 h-6 p-0 border-0 rounded cursor-pointer bg-transparent hover:bg-[var(--action-btn-hover)]"
                        :class="copiedIndex === index ? 'text-[var(--success-header-color)]' : 'text-[var(--primary-text-muted)]'"
                        aria-label="Copy code"
                        @click="copy(segment.code, index)"
                    >
                        <i class="pi" :class="copiedIndex === index ? 'pi-check' : 'pi-copy'" aria-hidden="true" />
                    </button>
                </div>
                <pre class="m-0 p-3 overflow-x-auto whitespace-pre-wrap [overflow-wrap:anywhere]"><code>{{ segment.code }}</code></pre>
            </div>
            <div v-else v-html="segment.html" />
        </template>
    </div>
</template>
